import React from "react";
import { Button } from "@chakra-ui/react";
import { colors } from "../../../styles";

type ButtonType =
  | "primary"
  | "secondary"
  | "assistive_1"
  | "assistive_2"
  | "assistive_3"
  | "alert"
  | "outline";

interface BaseButtonProps {
  type: ButtonType;
  size: "large" | "medium" | "small";
  rounded?: boolean;
  outlined?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

export const BaseButton: React.FC<BaseButtonProps> = ({
  type,
  size,
  rounded,
  outlined = false,
  children,
  onClick,
  className,
  disabled,
}) => {
  const sizeMapHeight = {
    large: 48,
    medium: 40,
    small: 32,
  } as const;

  // Map "outline" type to assistive_1 with outlined=true for backward compatibility
  const effectiveType = type === "outline" ? "assistive_1" : type;
  const effectiveOutlined = type === "outline" ? true : outlined;

  type ButtonStyleConfig = {
    background: string;
    color: string;
    border?: string;
    pressedBg: string; // hover와 pressed는 동일
    disabledBg?: string;
    disabledColor?: string;
  };

  const typeMap: Record<Exclude<ButtonType, "outline">, ButtonStyleConfig> = {
    primary: {
      background: colors.orange650,
      color: colors.white,
      pressedBg: colors.orange700,
      disabledBg: colors.gray200,
      disabledColor: colors.gray500,
    },
    secondary: {
      background: colors.goiBlack,
      color: colors.white,
      pressedBg: colors.gray800,
      disabledBg: colors.gray200,
      disabledColor: colors.gray500,
    },
    assistive_1: {
      background: colors.white,
      color: colors.gray600,
      pressedBg: colors.gray50,
      disabledBg: colors.white,
      disabledColor: colors.gray300,
    },
    assistive_2: {
      background: colors.gray50,
      color: colors.gray900,
      pressedBg: colors.gray100,
      disabledBg: colors.gray50,
      disabledColor: colors.gray400,
    },
    assistive_3: {
      background: colors.orange150,
      color: colors.orange650,
      pressedBg: colors.orange200,
      disabledBg: colors.orange100,
      disabledColor: colors.orange400,
    },
    alert: {
      background: "#FFEBEB",
      color: colors.danger,
      pressedBg: "#FFE0E0",
      disabledBg: colors.gray100,
      disabledColor: colors.gray400,
    },
  };

  const buttonStyle = typeMap[effectiveType];

  // Add border for assistive_1 when outlined
  const borderStyle =
    effectiveType === "assistive_1" && effectiveOutlined
      ? `1px solid ${colors.gray200}`
      : "none";

  return (
    <Button
      height={`${sizeMapHeight[size]}px`}
      width="100%"
      color={buttonStyle.color}
      backgroundColor={buttonStyle.background}
      border={borderStyle}
      borderRadius={rounded ? "24px" : "6px"}
      onClick={onClick}
      _hover={{
        backgroundColor: disabled
          ? buttonStyle.background
          : buttonStyle.pressedBg,
      }}
      _active={{
        backgroundColor: disabled
          ? buttonStyle.background
          : buttonStyle.pressedBg,
      }}
      _disabled={{
        backgroundColor: buttonStyle.disabledBg || buttonStyle.background,
        color: buttonStyle.disabledColor || buttonStyle.color,
        opacity: 1,
        cursor: "not-allowed",
      }}
      className={className}
      isDisabled={disabled}
    >
      {children}
    </Button>
  );
};
